import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export function appendRepeatQuery(params: URLSearchParams, key: string, values: string[]): void {
  for (const v of values) {
    if (v) params.append(key, v);
  }
}

export function ReportMultiSelect(props: {
  label: string;
  allLabel: string;
  options: string[];
  value: string[];
  onChange: (next: string[]) => void;
  loading?: boolean;
  error?: boolean;
  className?: string;
  optionLabels?: Record<string, string>;
}) {
  const { label, allLabel, options, value, onChange, loading, error, className, optionLabels } = props;
  const isOffice = label.toLowerCase().includes("office");
  const loadingText = isOffice ? "Loading offices..." : "Loading co codes...";
  const errorText = isOffice ? "Failed to load offices" : "Failed to load co codes";
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function toggle(opt: string) {
    if (value.includes(opt)) {
      onChange(value.filter((v) => v !== opt));
    } else {
      onChange([...value, opt]);
    }
  }

  const summary =
    value.length === 0 ? allLabel : value.length === 1 ? value[0] : `${value.length} selected`;

  return (
    <div className={cn("grid gap-1 min-w-[160px]", open && "z-20", className)} ref={rootRef}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="relative">
        <button
          type="button"
          className="flex h-10 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="truncate">{summary}</span>
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </button>
        {open && (
          <div
            className="absolute z-10 mt-1 max-h-40 w-full overflow-y-auto rounded-md border border-input bg-background px-3 py-2 text-sm shadow-lg"
            role="listbox"
            aria-multiselectable="true"
          >
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-3.5"
                checked={value.length === 0}
                onChange={() => onChange([])}
              />
              {allLabel}
            </label>
            {options.map((opt) => (
              <label key={opt} className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 size-3.5 shrink-0"
                  checked={value.includes(opt)}
                  onChange={() => toggle(opt)}
                />
                <span className="min-w-0 break-words">
                  {optionLabels?.[opt] ? (
                    <>
                      {opt}{" "}
                      <span className="text-muted-foreground">— {optionLabels[opt]}</span>
                    </>
                  ) : (
                    opt
                  )}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
      {loading && <div className="text-[10px] text-muted-foreground">{loadingText}</div>}
      {error && <div className="text-[10px] text-destructive">{errorText}</div>}
    </div>
  );
}
